import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, File, FileImage, FileText, LoaderCircle, Paperclip, Trash2, Upload } from "lucide-react";
import * as AnexoService from "@/services/AnexoService";
import { Anexo, EntidadeAnexo } from "@/services/AnexoService";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { IconeTile } from "@/components/ui/cores";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/utils/cn";

const LIMITE_BYTES = 20 * 1024 * 1024;

const tamanho = (bytes: number) =>
    bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;

const IconeArquivo = ({ tipo }: { tipo?: string }) => {
    const Icone = tipo?.startsWith('image/') ? FileImage : tipo === 'application/pdf' ? FileText : File;
    return <Icone className="h-5 w-5 shrink-0 text-muted-foreground" />;
}

type Envio = { nome: string; progresso: number };

type AnexosPanelProps = {
    entidade: EntidadeAnexo;
    // Sem registro (cadastro ainda não salvo) o painel só explica que é preciso salvar antes
    registro?: string;
    // Sem Card próprio, para usar dentro de um formulário
    embutido?: boolean;
    // Sem permissão de alterar o cadastro: só lista e baixa
    somenteLeitura?: boolean;
}

export const AnexosPanel = ({ entidade, registro, embutido, somenteLeitura }: AnexosPanelProps) => {
    const [anexos, setAnexos] = useState<Anexo[]>();
    const [envios, setEnvios] = useState<Envio[]>([]);
    const [arrastando, setArrastando] = useState(false);
    const [excluindo, setExcluindo] = useState<Anexo>();
    const [removendo, setRemovendo] = useState(false);
    const input = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!registro) return;
        AnexoService.listar(entidade, registro).then(r => setAnexos(r.data)).catch(() => setAnexos([]));
    }, [entidade, registro]);

    const enviar = (arquivos: FileList | null) => {
        if (!registro || !arquivos) return;
        Array.from(arquivos).forEach(arquivo => {
            if (arquivo.size > LIMITE_BYTES) {
                toast.error(`${arquivo.name} passa do limite de 20 MB.`);
                return;
            }
            const atualizar = (progresso: number) =>
                setEnvios(lista => lista.map(e => e.nome === arquivo.name ? { ...e, progresso } : e));
            setEnvios(lista => [...lista, { nome: arquivo.name, progresso: 0 }]);
            AnexoService.enviar(entidade, registro, arquivo, atualizar)
                .then(({ data }) => setAnexos(lista => [data, ...(lista || [])]))
                .catch(() => {})
                .finally(() => setEnvios(lista => lista.filter(e => e.nome !== arquivo.name)));
        });
        if (input.current) input.current.value = '';
    }

    const baixar = (anexo: Anexo) =>
        AnexoService.baixar(entidade, registro!, anexo).catch(() => toast.error(`Não foi possível baixar ${anexo.nome}.`));

    const excluir = () => {
        setRemovendo(true);
        AnexoService.excluir(entidade, registro!, excluindo!.id)
            .then(() => {
                setAnexos(lista => lista?.filter(a => a.id !== excluindo!.id));
                toast.success('Anexo excluído.');
            })
            .catch(() => {})
            .finally(() => {
                setRemovendo(false);
                setExcluindo(undefined);
            });
    }

    const conteudo = !registro ? (
        <p className="text-sm text-muted-foreground">Salve o cadastro para anexar arquivos.</p>
    ) : (
        <>
        {somenteLeitura ? (
            anexos?.length === 0 && <p className="text-sm text-muted-foreground">Nenhum arquivo anexado.</p>
        ) : (
        <div
            onDragOver={e => { e.preventDefault(); setArrastando(true); }}
            onDragLeave={() => setArrastando(false)}
            onDrop={e => { e.preventDefault(); setArrastando(false); enviar(e.dataTransfer.files); }}
            className={cn("flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-6 text-center transition-colors",
                arrastando ? "border-primary bg-primary/5" : "border-input")}
        >
            <Upload className="h-5 w-5 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Arraste arquivos para cá ou</p>
            <Button type="button" variant="outline" size="sm" onClick={() => input.current?.click()}>Escolher arquivos</Button>
            <p className="text-xs text-muted-foreground">Até 20 MB por arquivo</p>
            <input ref={input} type="file" multiple className="sr-only" aria-label="Escolher arquivos" onChange={e => enviar(e.target.files)} />
        </div>
        )}

        {(envios.length > 0 || !!anexos?.length) && (
            <ul className={cn("divide-y rounded-lg border", !somenteLeitura && "mt-3")}>
                {envios.map(envio => (
                    <li key={`envio-${envio.nome}`} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                        <LoaderCircle className="h-5 w-5 shrink-0 animate-spin text-muted-foreground" />
                        <span className="min-w-0 flex-1 truncate">{envio.nome}</span>
                        <span className="tabular-nums text-muted-foreground">{envio.progresso}%</span>
                    </li>
                ))}
                {anexos?.map(anexo => (
                    <li key={anexo.id} className="group flex items-center gap-3 px-3 py-2.5 text-sm">
                        <IconeArquivo tipo={anexo.tipoConteudo} />
                        <div className="min-w-0 flex-1">
                            <button type="button" onClick={() => baixar(anexo)} className="block max-w-full truncate text-left font-medium hover:text-primary hover:underline">
                                {anexo.nome}
                            </button>
                            <p className="text-xs text-muted-foreground">
                                {tamanho(anexo.tamanho)}, {new Date(anexo.data).toLocaleDateString('pt-BR')}{anexo.usuario?.nome && `, por ${anexo.usuario.nome}`}
                            </p>
                        </div>
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label={`Baixar ${anexo.nome}`} onClick={() => baixar(anexo)}>
                            <Download className="h-4 w-4" />
                        </Button>
                        {!somenteLeitura && (
                            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" aria-label={`Excluir ${anexo.nome}`} onClick={() => setExcluindo(anexo)}>
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        )}
                    </li>
                ))}
            </ul>
        )}

        <Dialog open={!!excluindo} onOpenChange={open => !open && !removendo && setExcluindo(undefined)}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Excluir anexo?</DialogTitle>
                    <DialogDescription>
                        O arquivo <span className="font-medium text-foreground">{excluindo?.nome}</span> será excluído definitivamente.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2 sm:gap-0">
                    <DialogClose asChild><Button type="button" variant="outline" disabled={removendo}>Cancelar</Button></DialogClose>
                    <Button type="button" variant="destructive" onClick={excluir} disabled={removendo}>
                        {removendo ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />} Excluir
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        </>
    );

    const titulo = (
        <h2 className="mb-4 flex items-center gap-2.5 text-base font-semibold">
            <IconeTile size="sm" cor="bg-slate-500/10 text-slate-600 dark:text-slate-300"><Paperclip /></IconeTile>
            Anexos
            {!!anexos?.length && <span className="text-sm font-normal text-muted-foreground">{anexos.length}</span>}
        </h2>
    );

    return embutido
        ? <div className="mb-4 mt-4 border-t pt-6">{titulo}{conteudo}</div>
        : <Card className="p-5 shadow-none">{titulo}{conteudo}</Card>;
}
